import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Shield, MessageCircle, Lock, CheckCircle2, Loader2, User as UserIcon } from 'lucide-react';
import PhoneOtpModal from '../../components/PhoneOtpModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffProfile() {
  const [loading, setLoading] = useState(true);
  const [security, setSecurity] = useState({ twoFactorEnabled: false, phoneVerified: false, isMicrosoft: false, phone: '', email: '' });
  const [waEnabled, setWaEnabled] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [otpOpen, setOtpOpen] = useState(false);
  const [saving2fa, setSaving2fa] = useState(false);

  const load = async () => {
    try {
      const [sec, pref] = await Promise.all([
        axios.get(`${API}/auth/me/security`),
        axios.get(`${API}/notification-preferences/me`),
      ]);
      setSecurity(sec.data);
      setPrefs(pref.data?.preferences || {});
      setWaEnabled(!!pref.data?.preferences?.whatsapp_enabled);
    } catch (e) {
      toast.error('Erreur de chargement du profil');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggle2FA = async (next) => {
    if (security.isMicrosoft) { toast.error('Compte Microsoft : authentification gérée par Microsoft.'); return; }
    setSaving2fa(true);
    try {
      await axios.put(`${API}/auth/me/2fa`, { enabled: next });
      setSecurity(s => ({ ...s, twoFactorEnabled: next }));
      toast.success(next ? '2FA activée ✅' : '2FA désactivée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec mise à jour 2FA');
    }
    setSaving2fa(false);
  };

  const enableWhatsApp = async () => {
    try {
      await axios.put(`${API}/notification-preferences/me`, { ...prefs, whatsapp_enabled: true });
      setWaEnabled(true);
      setPrefs(p => ({ ...p, whatsapp_enabled: true }));
      toast.success('Alertes WhatsApp activées ✅');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec d'activation");
    }
  };

  const toggleWhatsApp = async (next) => {
    if (next && !security.phoneVerified) { setOtpOpen(true); return; }
    if (next) { await enableWhatsApp(); return; }
    try {
      await axios.put(`${API}/notification-preferences/me`, { ...prefs, whatsapp_enabled: false });
      setWaEnabled(false);
      setPrefs(p => ({ ...p, whatsapp_enabled: false }));
      toast.success('Alertes WhatsApp désactivées');
    } catch (e) { toast.error('Échec mise à jour'); }
  };

  const onVerified = async () => {
    setSecurity(s => ({ ...s, phoneVerified: true }));
    await enableWhatsApp();
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="staff-profile-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center"><UserIcon className="w-6 h-6 text-emerald-700" /></div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mon profil</h1>
          <p className="text-sm text-gray-500">{security.email}{security.isMicrosoft ? ' · Compte Microsoft' : ' · Compte local'}</p>
        </div>
      </div>

      {/* Alertes WhatsApp */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-600" />Alertes WhatsApp</CardTitle>
          <CardDescription>Recevez vos alertes opérationnelles (demandes, contre-offres, opérations assignées, documents manquants…) directement sur WhatsApp.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Notifications WhatsApp</p>
              {security.phoneVerified
                ? <p className="text-xs text-gray-500">{security.phone || 'Numéro vérifié'}</p>
                : <button onClick={() => setOtpOpen(true)} className="text-xs text-amber-600 hover:underline flex items-center gap-1" data-testid="staff-whatsapp-verify-link"><Lock className="w-3 h-3" />Vérifiez votre numéro pour activer</button>}
            </div>
            <Switch checked={waEnabled} onCheckedChange={toggleWhatsApp} data-testid="staff-whatsapp-toggle" />
          </div>
        </CardContent>
      </Card>

      {/* Sécurité 2FA */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-red-600" />Sécurité — Double authentification (2FA)</CardTitle>
          <CardDescription>Ajoutez un code de vérification (WhatsApp ou Email) à votre connexion pour protéger votre compte.</CardDescription>
        </CardHeader>
        <CardContent>
          {security.isMicrosoft ? (
            <div className="flex items-center gap-2 p-4 border rounded-lg bg-blue-50 text-blue-800 text-sm">
              <CheckCircle2 className="w-4 h-4" />Votre authentification est gérée par Microsoft. La 2FA Baobab ne s'applique pas.
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium flex items-center gap-2">Authentification à deux facteurs
                  {security.twoFactorEnabled && <Badge className="bg-emerald-600">Activée</Badge>}
                </p>
                <p className="text-xs text-gray-500">Un code OTP vous sera demandé à chaque connexion.</p>
              </div>
              <Switch checked={security.twoFactorEnabled} disabled={saving2fa} onCheckedChange={toggle2FA} data-testid="staff-2fa-toggle" />
            </div>
          )}
        </CardContent>
      </Card>

      <PhoneOtpModal open={otpOpen} onClose={() => setOtpOpen(false)} defaultPhone={security.phone} onVerified={onVerified} />
    </div>
  );
}
